import React from "react";
import type Highcharts from "highcharts";
import type { MinMax } from "@ui/Highcharts/hooks/useAxisMinMax";
import useAxisMinMax from "@ui/Highcharts/hooks/useAxisMinMax";
import isFuncState from "../../../../lib/isFuncState";
import useAnalysisKeyboardSelect from "../../Chart/hooks/useAnalysisKeyboardSelect";
import useAnalysisGroupPlotLines from "../../Chart/hooks/useAnalysisGroupPlotLines";
import useGroups from "../../../../api/useGroups";
import useAnalysisSelectionPlotLines from "../../Chart/hooks/useAnalysisSelectionPlotLines";
import useAnalysisMouseSelect from "../../Chart/hooks/useAnalysisMouseSelect";
import type { Project } from "../../../../api/useProjects";
import type { Source } from "../../../../api/useSources";
import type { Analysis } from "../../../../api/Analysis";
import useFaultEventPlotBands from "./useFaultEventPlotBands";
import { useEventPeriod } from "./useEventPeriod";
import type { Event } from "../../../../api/useEvents";
import { getEventEndsAt } from "../../../../domain/event";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";

const AXIS_ID = "period-axis";

const useAnalysisXAxis = ({
  chartRef,
  project,
  source,
  analyses,
  boundaries,
  event,
}: {
  project: Project;
  source: Source;
  analyses: Analysis[];
  chartRef: React.RefObject<Highcharts.Chart>;
  boundaries: "active-analyses" | "analyses";
  event: Event;
}) => {
  const projectId = project.id;
  const [faultEventPeriod, setFaultEventPeriod, projectExtremes] = useEventPeriod(
    boundaries === "analyses"
      ? {
          event,
          source,
          project,
          boundaries: "analyses",
        }
      : {
          event,
          source,
          project,
          analyses: analyses as ActiveAnalysis[],
          boundaries: "active-analyses",
        },
  );
  const endsAt = getEventEndsAt(event);

  useFaultEventPlotBands({
    chartRef,
    faultEventPeriod: { from: event.startsAt.getTime(), to: endsAt.getTime() },
    projectExtremes,
  });

  useAnalysisGroupPlotLines({
    chartRef,
    groups: useGroups(projectId),
    period: faultEventPeriod,
    setPeriod: setFaultEventPeriod,
  });

  useAnalysisSelectionPlotLines({
    chartRef,
    boundaries,
    analyses,
    projectId,
    period: faultEventPeriod,
    timeZone: project.timezone,
  });
  useAnalysisKeyboardSelect({ chartRef, analyses, projectId });
  useAnalysisMouseSelect({ projectId, analyses, chartRef });

  const minMax = React.useMemo(
    () => ({ min: faultEventPeriod.from, max: faultEventPeriod.to }),
    [faultEventPeriod],
  );
  const setMinMax = React.useCallback(
    (value: React.SetStateAction<MinMax>) => {
      if (isFuncState(value)) {
        setFaultEventPeriod(oldPeriod => {
          const newPeriod = value({ min: oldPeriod.from, max: oldPeriod.to });
          return { from: Number(newPeriod.min), to: Number(newPeriod.max) };
        });
      } else {
        setFaultEventPeriod({ from: Number(value.min), to: Number(value.max) });
      }
    },
    [setFaultEventPeriod],
  );

  const initialMinMax = useAxisMinMax({
    chartRef,
    minMax,
    setMinMax,
    axisId: AXIS_ID,
  });

  return React.useMemo(
    () =>
      ({
        ...initialMinMax,
        min: projectExtremes.from,
        id: AXIS_ID,
        type: "datetime" as const,
        tickPixelInterval: 150, // default 100 is too dense for 'DD/MM/YYYY'
      }) as const,
    [initialMinMax, projectExtremes],
  );
};
export default useAnalysisXAxis;
